import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp, NativeStackScreenProps } from '@react-navigation/native-stack';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Picker } from '@/components/Picker';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import WebView, { WebViewMessageEvent } from '@/components/Webview';
import { ds } from '@/components/lib/design.system';
import { URLS } from '@/data/config';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useDepositFraudRisk } from '@/feature/deposit/hooks/use-deposit-fraud-risk';
import { STATES } from '@/feature/kyc/const';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useActiveWallet, useActiveWalletPaysafeCredentials } from '@/hooks/use-active-wallet';
import { useAuthUserInfo } from '@/hooks/use-auth-user-info';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useRouteByName } from '@/hooks/use-route-by-name';
import { DepositStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { logger } from '@/utils/logging';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { DebitCardAdditionFormHTML } from '../components/debit-deposit-form/DebitCardAdditionFormHTML';
import { useDepositStatusStore } from '../hooks/use-deposit-status-store';
import {
    ADD_DEBIT_CARD_LOG_TAG,
    MESSAGE_TYPES,
    MessageType,
    PAYSAFE_ERROR_TYPES,
    PaysafeErrorType,
    handleBadBinMessage,
    handleDepositSuccessMessage,
    handlePaysafeErrorMessage,
    parseDebitCardFormMessage,
} from '../webview-message-handler';

const styles = StyleSheet.create({
    indicatorStyle: {
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: ds.colorValue.background.neutral.base,
    },
});

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'AddDebitCardWebView'>;

const getStateLabel = (value: string) =>
    STATES.reduce((label, state) => (state.value === value ? state.label : label), '');

export const AddDebitCardWebView = ({ route }: ScreenProps) => {
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const [showPicker, setShowPicker] = useState(false);
    const [selectedStateValue, setSelectedStateValue] = useState('');

    const webviewRef = useRef<WebView>(null);

    const { t } = useTranslation('wallet');
    const navigation = useNavigation<NativeStackNavigationProp<DepositStackParamList>>();
    const depositRoute = useRouteByName<DepositStackParamList, 'DepositScreen'>('DepositScreen');
    const forceDepositWithDebitCard = depositRoute?.params?.forceDepositWithDebitCard;

    // Due to the `staleTime` being set to `useAuthUserInfo` query, outdated data can be returned even immediately after KYC (when the user’s information is updated). By setting `refetchOnMount` to “always”, we force a refetch to ensure that the most up-to-date data is always retrieved.
    const { data: info, isLoading: userInfoLoading } = useAuthUserInfo({ refetchOnMount: 'always' });
    const { activeWalletProductName: productName, isSharedSocialWallet } = useActiveWallet();
    const { data: apiKeys, isLoading: apiKeysLoading } = useActiveWalletPaysafeCredentials();
    const { showInfoSheet } = useAlerts();
    const userSettings = useJurisdictionStore(
        state => state.jurisdictionSettings?.globalSettings?.keyValuePairs?.UserSettings
    );
    const jurisdictionSettings = useJurisdictionStore(state => state.jurisdictionSettings);
    const { data: fraudRisk, isLoading: fraudRiskLoading } = useDepositFraudRisk();

    const is3dsEnabledOnAddingCards =
        jurisdictionSettings?.productConfig?.settings?.enable_3ds2_on_adding_cards ?? false;

    const shouldTrigger3ds = is3dsEnabledOnAddingCards && route.params?.canUse3ds && fraudRisk?.data.should_trigger_3ds;

    const params = {
        productName,
        selectedAmount: Number(selectedAmount) ?? 0,
        username: apiKeys?.paysafe_username ?? '',
        pubKey: apiKeys?.paysafe_pub_key ?? '',
        paysafeAccountId: Number(route.params?.paysafeAccountId) ?? 0,
        paysafeEnv: URLS.PAYSAFE_ENV,
        paysafeSdk: URLS.PAYSAFE_SDK,
        userInfo: JSON.stringify(info),
        badBinThreshold: Number(userSettings?.bad_bin_threshold) ?? 0,
        shouldTrigger3ds: shouldTrigger3ds ?? false,
        titleStr: t('add_debit_card_title') ?? '',
        subtextStr:
            t('add_debit_card_subtext', {
                selected_amount: toLocaleCurrency(selectedAmount) ?? 0,
            }) ?? '',
        isSharedSocialWallet: isSharedSocialWallet,
    };

    const trackCardSubmit = () => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.DEPOSIT_DEBITJS_SUBMIT, {
            kycCTADebitOnly: forceDepositWithDebitCard,
        });
    };

    const messageHandlers: Record<MessageType, (data?: any) => void> = {
        [MESSAGE_TYPES.SHOW_PICKER]: () => {
            setShowPicker(true);
        },
        [MESSAGE_TYPES.BAD_BIN]: () => {
            handleBadBinMessage(showInfoSheet, navigation);
        },
        [MESSAGE_TYPES.STATE]: (usState: string) => {
            setSelectedStateValue(usState);
        },
        [MESSAGE_TYPES.DEPOSIT_SUCCESS]: (token: string) => {
            trackCardSubmit();
            handleDepositSuccessMessage(token, 'USD', shouldTrigger3ds ?? false, navigation);
        },
        [MESSAGE_TYPES.DEPOSIT_ERROR]: (data: { type: PaysafeErrorType; error: unknown }) => {
            trackCardSubmit();
            handlePaysafeErrorMessage(data, selectedAmount, navigation);
        },
    };

    const handleWebViewMessage = (event: WebViewMessageEvent) => {
        const parsedEvent = parseDebitCardFormMessage(event.nativeEvent.data);
        if (!parsedEvent) {
            logger.debug(ADD_DEBIT_CARD_LOG_TAG, 'Ignoring non-form message', event.nativeEvent.data);
            return;
        }
        try {
            messageHandlers[parsedEvent.type](parsedEvent.payload);
        } catch (err) {
            logger.error(ADD_DEBIT_CARD_LOG_TAG, 'Error handling event from webview', err);
            handlePaysafeErrorMessage({ type: PAYSAFE_ERROR_TYPES.GENERIC, error: err }, selectedAmount, navigation); // assume it's a `generic` error
        }
    };

    const sendAmericanStateToWebView = (state: string) => {
        if (webviewRef.current) {
            setSelectedStateValue(state);
            webviewRef.current.postMessage('state:' + state + ':' + getStateLabel(state)); // eg. "state:CA:California"
        }
    };

    const isInitialLoading = userInfoLoading || apiKeysLoading || fraudRiskLoading;

    return (
        <Screen edges={['bottom', 'top']}>
            <ScreenNavBar
                closeIconMode="back"
                handlePressTracking={() => {
                    BetrAnalytics.trackProductEvent(AnalyticsEvent.DEPOSIT_DEBITJS_ABORT);
                }}
                title={t('add_debit_card_title')}
                onClose={route.params.closeIconHandlePress}
            />
            <MaxWidthWrapper flex={1}>
                {isInitialLoading ? (
                    <View style={styles.indicatorStyle}>
                        <LoadingSpinner />
                    </View>
                ) : (
                    <View style={common.flex}>
                        <WebView
                            ref={webviewRef}
                            style={[common.backgroundColorTransparent, common.flex]}
                            scalesPageToFit={Platform.OS !== 'ios'}
                            originWhitelist={['*']}
                            onMessage={handleWebViewMessage}
                            source={{ html: DebitCardAdditionFormHTML(params) }}
                        />
                    </View>
                )}

                {showPicker ? (
                    <Picker
                        items={STATES}
                        pickedValue={selectedStateValue}
                        closePicker={() => setShowPicker(false)}
                        handleValueChange={sendAmericanStateToWebView}
                    />
                ) : null}
            </MaxWidthWrapper>
        </Screen>
    );
};
